import {
  IMapboxAutocompleteAddressesResponse,
  ISuggestedAddress,
} from '@monofront/types';

interface IGetAddressGeolocation {
  mapboxToken?: string;
  address: string;
  countryCode: string;
}

interface IGetAddressAutocomplete {
  mapboxToken?: string;
  writtenStreet: string;
  locale: string;
  sessionToken: string;
  searchableCountry: string;
  proximity: [string, string];
}

interface IGetMapBoxGeocodingFirstCenter {
  mapboxToken?: string;
  writtenStreet: string;
  locale: string;
  sessionToken: string;
  searchableCountry: string;
}

export default async function getAddressGeolocation(
  { mapboxToken, address, countryCode }: IGetAddressGeolocation,
  logger,
) {
  const country = countryCode === 'MC' ? 'FR' : countryCode;

  try {
    const responseList = (
      await (
        await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            address,
          )}.json?limit=1&access_token=${mapboxToken}&country=${country}`,
        )
      ).json()
    ).features;

    // Do not return geoloc data if address has not been found
    if (!responseList.length) {
      logger.warn('No geocoding data found for the given address', {
        countryCode,
      });

      return [null, null];
    }

    const {
      center: [lng, lat],
      relevance,
    } = responseList[0];

    // Do not return geoloc data if address has a low relevance notation.
    // 0.75 has been choosen arbitrarily
    if (relevance < 0.75) {
      logger.warn('The address found for geocoding does not seem relevant', {
        relevance,
        countryCode,
      });

      return [null, null];
    }

    return [lng, lat];
  } catch (error) {
    logger.error('Error while geocoding the given address', {
      error,
      countryCode,
    });
    throw Error(error as string);
  }
}

export const getMapBoxAutoCompleteAddresses = async (
  {
    mapboxToken,
    writtenStreet,
    locale,
    sessionToken, // might be persisted if too many sessions created
    searchableCountry,
    proximity,
  }: IGetAddressAutocomplete,
  logger,
): Promise<ISuggestedAddress[]> => {
  const urlParams = new URLSearchParams();

  urlParams.set('q', writtenStreet);
  urlParams.set('country', searchableCountry);
  urlParams.set('session_token', sessionToken);
  urlParams.set('types', 'address, street');
  urlParams.set('language', locale);
  urlParams.set('access_token', mapboxToken || '');
  urlParams.set('proximity', proximity?.join(','));

  const response = await fetch(
    'https://api.mapbox.com/search/searchbox/v1/suggest?' +
      urlParams.toString(),
  );
  const mapBoxAddressesResponse: IMapboxAutocompleteAddressesResponse =
    await response.json();

  const suggestions = mapBoxAddressesResponse.suggestions.map(
    ({
      full_address,
      name,
      address,
      feature_type,
      place_formatted,
      context: { place, postcode, country, street },
    }) => {
      if (feature_type === 'street') {
        return {
          street: street?.name,
          city: place?.name,
          postcode: postcode?.name,
          label: `${name}, ${place_formatted}`,
          countryCode: country?.country_code,
          type: 'street' as ISuggestedAddress['type'],
        };
      }

      return {
        street: address,
        city: place?.name,
        postcode: postcode?.name,
        label: full_address,
        countryCode: country?.country_code,
        type: 'address' as ISuggestedAddress['type'],
      };
    },
  );

  if (suggestions.length === 0) {
    logger.warn(
      'The autocomplete addresses for the given query are empty, no suggestions found',
      { searchableCountry },
    );
  }

  return suggestions;
};

export const getMapBoxGeocodingFirstCenter = async ({
  mapboxToken,
  writtenStreet,
  locale,
  sessionToken, // might be persisted if too many sessions created
  searchableCountry,
}: IGetMapBoxGeocodingFirstCenter): Promise<[string, string] | undefined> => {
  const urlParams = new URLSearchParams();

  urlParams.set('country', searchableCountry);
  urlParams.set('session_token', sessionToken);
  urlParams.set('language', locale);
  urlParams.set('proximity', 'ip');
  urlParams.set('access_token', mapboxToken || '');

  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      writtenStreet,
    )}.json?` + urlParams.toString(),
  );
  const mapBoxGeocodingResponse = await response.json();

  return mapBoxGeocodingResponse.features?.[0]?.center;
};
